// sessionStorage survives a reload, so a hard refresh will NOT surface a saved change — every
// chart write must invalidate its cache entries explicitly.

const CHART_META_PREFIX = 'chart_meta_';
const CHART_DATA_PREFIX = 'chart_data_';
const CACHE_OWNER_KEY = 'zi_chart_cache_owner';
const AUTH_EPOCH_KEY = 'zi_auth_epoch';
const RETRIEVAL_OVERRIDE_KEY = 'prev_dataRetrieval_Override';

export const chartMetaCacheKey = (chartId: number | string): string =>
	`${CHART_META_PREFIX}${chartId}`;

export const chartDataCacheKey = (chartId: number | string): string =>
	`${CHART_DATA_PREFIX}${chartId}`;

export const clearChartMetaSessionCache = (chartId: number | undefined): void => {
	if (chartId === undefined) return;

	try {
		sessionStorage.removeItem(chartMetaCacheKey(chartId));
	} catch (e) {
		// ignore storage errors
	}
};

export const clearChartSessionCache = (chartId: number | undefined): void => {
	if (chartId === undefined) return;

	clearChartMetaSessionCache(chartId);

	try {
		sessionStorage.removeItem(chartDataCacheKey(chartId));
	} catch (e) {
		// ignore storage errors
	}
};

export const clearAllChartSessionCache = (): void => {
	try {
		// Drop the owner stamp first: if a removal below throws, the cache is left unowned and the
		// next claim retries, rather than being stamped as belonging to whoever arrives next.
		sessionStorage.removeItem(CACHE_OWNER_KEY);

		Object.keys(sessionStorage)
			.filter((key) => key.startsWith(CHART_META_PREFIX) || key.startsWith(CHART_DATA_PREFIX))
			.forEach((key) => sessionStorage.removeItem(key));

		// Holds the previous user's retrieval override, including the dimensions they were
		// restricted to, and lives in localStorage so it outlives even the tab.
		localStorage.removeItem(RETRIEVAL_OVERRIDE_KEY);
	} catch (e) {
		// ignore storage errors
	}
};

// Cached rows are keyed by chart id alone, but retrieval is filtered by the signed-in user's own
// dimension restrictions, so two users entitled to different rows of one chart share a key.
// Claim on identity, not on login: SSO, MFA and a crash-restored tab never pass through the login
// form.
export const claimChartSessionCache = (userId: number | undefined): void => {
	// Unknown identity clears and declines to stamp — an unrecognised /app/me shape must not leave
	// the previous user's rows readable.
	if (userId === undefined || userId === null) {
		clearAllChartSessionCache();
		return;
	}

	try {
		const owner = sessionStorage.getItem(CACHE_OWNER_KEY);
		if (owner === String(userId)) return;

		clearAllChartSessionCache();
		signalChartSessionCacheReset();
		sessionStorage.setItem(CACHE_OWNER_KEY, String(userId));
	} catch (e) {
		// ignore storage errors
	}
};

// sessionStorage is per-tab, so clearing it cannot reach a second tab the user left open on a
// dashboard. A localStorage write is the only cross-tab signal available; other tabs evict when
// the event arrives. The listener never writes, so this cannot ping-pong between tabs.
export const signalChartSessionCacheReset = (): void => {
	try {
		localStorage.setItem(AUTH_EPOCH_KEY, `${Date.now()}`);
	} catch (e) {
		// ignore storage errors
	}
};

export const handleChartSessionCacheResetEvent = (event: StorageEvent): void => {
	if (event.key === AUTH_EPOCH_KEY) {
		clearAllChartSessionCache();
	}
};
